import { useState } from 'react';
import { Plus, Trash2, Clock, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useZoneTimeWindowsAdmin, type ZoneWindowRow } from '@/hooks/useZoneTimeWindows';

const DAY_LABELS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'] as const;

interface ZoneScheduleSettingsProps {
  establishmentId: string;
  zoneId: string;
  zoneName: string;
}

/**
 * Carte « Horaires propres à cette salle » — visible dans Établissement → Zones.
 * La page Établissement est réservée aux administrateurs (redirection sinon),
 * donc aucun contrôle de rôle supplémentaire ici ; la RLS impose l'admin.
 */
export function ZoneScheduleSettings({ establishmentId, zoneId, zoneName }: ZoneScheduleSettingsProps) {
  const { toast } = useToast();
  const { windows, loading, createWindow, updateWindow, deleteWindow } =
    useZoneTimeWindowsAdmin(establishmentId);

  const zoneWindows = windows.filter((w) => w.zoneId === zoneId);

  const [startTime, setStartTime] = useState('06:00');
  const [endTime, setEndTime] = useState('22:30');
  const [days, setDays] = useState<number[]>([1, 2, 3, 4]);

  const toggleDay = (day: number) => {
    setDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b),
    );
  };

  const handleAdd = async () => {
    if (days.length === 0) {
      toast({
        title: 'Jours manquants',
        description: 'Sélectionnez au moins un jour de la semaine.',
        variant: 'destructive',
      });
      return;
    }
    if (endTime <= startTime) {
      toast({
        title: 'Horaires invalides',
        description: "L'heure de fermeture doit être après l'heure d'ouverture (les plages passant minuit ne sont pas supportées).",
        variant: 'destructive',
      });
      return;
    }
    try {
      await createWindow.mutateAsync({
        zoneId,
        startTime,
        endTime,
        availableDays: days,
        isActive: true,
      });
      toast({ title: 'Plage ajoutée', description: `${zoneName} : ${startTime}–${endTime}` });
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err instanceof Error ? err.message : 'Création impossible.',
        variant: 'destructive',
      });
    }
  };

  const handleToggleActive = async (window: ZoneWindowRow, isActive: boolean) => {
    try {
      await updateWindow.mutateAsync({
        id: window.id,
        zoneId: window.zoneId,
        startTime: window.startTime,
        endTime: window.endTime,
        availableDays: window.availableDays,
        isActive,
      });
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err instanceof Error ? err.message : 'Mise à jour impossible.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteWindow.mutateAsync(id);
      toast({ title: 'Plage supprimée' });
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err instanceof Error ? err.message : 'Suppression impossible.',
        variant: 'destructive',
      });
    }
  };

  const formatDays = (availableDays: number[]) =>
    availableDays.length === 7
      ? 'Tous les jours'
      : [...availableDays].sort((a, b) => a - b).map((d) => DAY_LABELS[d]).join(', ');

  return (
    <div className="mt-2 rounded-lg border border-dashed p-3 space-y-3 bg-background">
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-muted-foreground" />
        <Label className="text-sm font-medium">Horaires propres à cette salle</Label>
      </div>

      {loading ? (
        <p className="text-xs text-muted-foreground">Chargement…</p>
      ) : zoneWindows.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Aucune plage : cette salle suit les services du restaurant.
        </p>
      ) : (
        <div className="space-y-2">
          {zoneWindows.map((w) => (
            <div key={w.id} className="flex items-center gap-2 text-sm">
              <Switch
                checked={w.isActive}
                onCheckedChange={(checked) => handleToggleActive(w, checked)}
                aria-label="Activer la plage"
              />
              <span className={w.isActive ? '' : 'text-muted-foreground line-through'}>
                {w.startTime}–{w.endTime}
              </span>
              <span className="text-xs text-muted-foreground flex-1">{formatDays(w.availableDays)}</span>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-destructive"
                onClick={() => handleDelete(w.id)}
                aria-label="Supprimer la plage"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2 pt-2 border-t">
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor={`zs-start-${zoneId}`} className="text-xs">Ouverture</Label>
            <Input
              id={`zs-start-${zoneId}`}
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="h-8 w-24"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`zs-end-${zoneId}`} className="text-xs">Fermeture</Label>
            <Input
              id={`zs-end-${zoneId}`}
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="h-8 w-24"
            />
          </div>
          <Button
            size="sm"
            onClick={handleAdd}
            disabled={createWindow.isPending || days.length === 0}
            className="h-8"
          >
            <Plus className="w-4 h-4 mr-1" />
            Ajouter
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {DAY_LABELS.map((label, day) => (
            <button
              key={day}
              type="button"
              onClick={() => toggleDay(day)}
              className={`px-2 py-1 text-xs rounded border transition-colors ${
                days.includes(day)
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted/40 text-muted-foreground border-transparent hover:border-border'
              }`}
              aria-pressed={days.includes(day)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
        <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <AlertDescription className="text-blue-800 dark:text-blue-300 text-xs">
          L'heure de fin est l'heure de <strong>fermeture</strong> de la salle. La dernière
          réservation proposée dépend de la durée : avec des réservations de 3 h, la dernière
          heure proposée est 3 h avant la fermeture. Sans plage enregistrée, la salle suit
          les services du restaurant.
        </AlertDescription>
      </Alert>
    </div>
  );
}
